import React, { useState, useEffect } from 'react';
import { notifyCartUpdated } from '../../utils/cartEvents';
import { useHistory } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Plus, Minus, ShoppingBag, ShoppingCart, Package, ArrowRight, Search, AlertCircle, Upload } from 'lucide-react';
import UserLayout from './UserLayout';
import { showNotification, showError } from '../../utils/notificationHandler';
import { useOrderingEnabled } from '../../hooks/useOrderingEnabled';

const CartItem = ({ item, updateQuantity, removeFromCart }) => {
  const [inputQuantity, setInputQuantity] = useState(item.quantity);

  const handleQuantityInputChange = (e) => {
    const value = e.target.value;
    if (value === '' || (!isNaN(value) && parseInt(value) >= 0)) {
      setInputQuantity(value);
    }
  };

  const handleQuantityInputBlur = () => {
    const newQuantity = parseInt(inputQuantity) || 1;
    if (newQuantity !== item.quantity) {
      updateQuantity(item.partNumber, newQuantity);
    }
    setInputQuantity(newQuantity);
  };

  const handleQuantityInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleQuantityInputBlur();
    }
  };

  // Update input when item quantity changes (from + or - buttons)
  React.useEffect(() => {
    setInputQuantity(item.quantity);
  }, [item.quantity]);

  const itemTotal = item.price ? (parseFloat(item.price) * item.quantity).toFixed(2) : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4"
    >
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        {/* Product Info */}
        <div className="flex-1">
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
              <Package className="w-8 h-8 text-gray-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{item.partNumber}</h3>
              <p className="text-sm text-gray-600 mt-1">{item.description}</p>
              {item.isBOM && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                  BOM Item
                </span>
              )}
              {item.comment && (
                <p className="text-sm text-blue-600 mt-1 italic">Note: {item.comment}</p>
              )}
            </div>
          </div>
        </div>

        {/* Quantity Controls */}
        <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
          
          {/* Price Display */}
          {item.price && (
            <div className="text-center">
              <p className="text-sm text-gray-600">Unit Price</p>
              <p className="text-lg font-semibold text-gray-900">${parseFloat(item.price).toFixed(2)}</p>
            </div>
          )}

          {/* Quantity Controls */}
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-700">Qty:</span>
            <div className="flex items-center border border-gray-300 rounded-lg">
              <button
                onClick={() => updateQuantity(item.partNumber, item.quantity - 1)}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors duration-200 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand"
                disabled={item.quantity <= 1}
                aria-label="Decrease quantity"
              >
                <Minus size={16} />
              </button>
              <input
                type="text"
                value={inputQuantity}
                onChange={handleQuantityInputChange}
                onBlur={handleQuantityInputBlur}
                onKeyPress={handleQuantityInputKeyPress}
                className="w-16 text-center py-2 border-0 tabular-nums focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand text-gray-900 font-medium"
                min="1"
                aria-label="Quantity"
              />
              <button
                onClick={() => updateQuantity(item.partNumber, item.quantity + 1)}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors duration-200 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand"
                aria-label="Increase quantity"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          {/* Total Price */}
          {itemTotal && (
            <div className="text-center">
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-xl font-bold text-green-600">${itemTotal}</p>
            </div>
          )}

          {/* Remove Button */}
          <button
            onClick={() => removeFromCart(item.partNumber)}
            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
            title="Remove from cart"
            aria-label="Remove from cart"
          >
            <Trash2 size={20} aria-hidden="true" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const CartSummary = ({ cartItems }) => {
  const itemsWithPrices = cartItems.filter(item => item.price);
  const totalValue = itemsWithPrices.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  if (itemsWithPrices.length === 0) return null;

  return (
    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Quote Summary</h3>
      <div className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Total Items:</span>
          <span className="font-medium text-gray-900">{totalItems}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Items with Pricing:</span>
          <span className="font-medium text-gray-900">{itemsWithPrices.length}</span>
        </div>
        <div className="border-t border-gray-200 pt-3">
          <div className="flex justify-between">
            <span className="text-base font-medium text-gray-900">Estimated Total:</span>
            <span className="text-xl font-bold text-green-600">${totalValue.toFixed(2)}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            *Pricing shown is estimated. Final pricing will be confirmed in your quote.
          </p>
        </div>
      </div>
    </div>
  );
};

const EmptyCartState = ({ onContinueShopping, onBulkUpload }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <ShoppingCart className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
    <p className="text-gray-600 mb-6">Start adding parts to build your quote</p>
    <div className="flex flex-col sm:flex-row gap-4 justify-center">
      <button
        onClick={onContinueShopping}
        className="inline-flex items-center px-6 py-3 bg-brand text-brand-dark rounded-lg font-semibold hover:bg-brand-hover transition-colors duration-200"
      >
        <Search className="w-5 h-5 mr-2" />
        Browse Parts
      </button>
      <button
        onClick={onBulkUpload}
        className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 font-medium"
      >
        <Upload className="w-5 h-5 mr-2" />
        Bulk Upload
      </button>
    </div>
  </div>
);

const UserCart = () => {
  const { enabled: orderingEnabled, message: orderingMessage } = useOrderingEnabled();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  useEffect(() => {
    const loadCart = () => {
      try {
        const storedCart = localStorage.getItem('cart');
        if (storedCart) {
          const parsedCart = JSON.parse(storedCart);
          setCartItems(Array.isArray(parsedCart) ? parsedCart : []);
        }
      } catch (error) {
        console.error('Error loading cart:', error);
        setCartItems([]);
      } finally {
        setLoading(false);
      }
    };

    loadCart();
  }, []);

  const updateQuantity = (partNumber, newQuantity) => {
    if (newQuantity < 1) return;
    
    const updatedCart = cartItems.map(item =>
      item.partNumber === partNumber ? { ...item, quantity: newQuantity } : item
    );
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    notifyCartUpdated();
    showNotification('Quantity updated successfully', 'success');
  };

  const removeFromCart = (partNumber) => {
    const updatedCart = cartItems.filter(item => item.partNumber !== partNumber);
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    notifyCartUpdated();
    showNotification('Item removed from cart successfully', 'success');
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('cart');
    notifyCartUpdated();
    showNotification('Cart cleared successfully', 'success');
  };

  const handleProceedToCheckout = () => {
    if (!orderingEnabled) {
      showNotification(orderingMessage, 'error');
      return;
    }
    if (cartItems.length > 0) {
      history.push('/user/checkout');
    } else {
      showNotification('Your cart is empty. Add items before proceeding.', 'error');
    }
  };

  const handleContinueShopping = () => {
    history.push('/user/part-enquiry');
  };

  const handleBulkUpload = () => {
    history.push('/user/bulk-upload');
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
          <span className="ml-3 text-gray-600">Loading cart...</span>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quote Cart</h1>
            <nav className="text-sm text-gray-600 mt-1">
              <button 
                onClick={() => history.push('/user/dashboard')}
                className="hover:text-brand transition-colors duration-200"
              >
                Dashboard
              </button>
              <span className="mx-2">{'>'}</span>
              <span>Cart</span>
            </nav>
          </div>
          
          {cartItems.length > 0 && (
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <span className="text-sm text-gray-600">
                {cartItems.length} item{cartItems.length !== 1 ? 's' : ''}
              </span>
              <button
                onClick={clearCart}
                className="text-sm text-red-600 hover:text-red-800 font-medium"
              >
                Clear All
              </button>
            </div>
          )}
        </div>

        {cartItems.length === 0 ? (
          <EmptyCartState onContinueShopping={handleContinueShopping} onBulkUpload={handleBulkUpload} />
        ) : (
          <>
            {/* Cart Items */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Items in your cart</h2>
              <AnimatePresence>
                {cartItems.map(item => (
                  <CartItem
                    key={item.partNumber}
                    item={item}
                    updateQuantity={updateQuantity}
                    removeFromCart={removeFromCart}
                  />
                ))}
              </AnimatePresence>
            </div>

            {/* Cart Summary */}
            <CartSummary cartItems={cartItems} />

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleContinueShopping}
                className="flex-1 flex items-center justify-center py-3 px-6 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 font-medium"
              >
                <Search className="w-5 h-5 mr-2" />
                Continue Shopping
              </button>
              
              <button
                onClick={handleBulkUpload}
                className="flex-1 flex items-center justify-center py-3 px-6 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 font-medium"
              >
                <Upload className="w-5 h-5 mr-2" />
                Bulk Upload
              </button>
              
              <button
                onClick={handleProceedToCheckout}
                disabled={!orderingEnabled}
                className="flex-1 flex items-center justify-center py-3 px-6 bg-brand text-brand-dark rounded-lg font-semibold hover:bg-brand-hover transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ShoppingBag className="w-5 h-5 mr-2" />
                Proceed to Checkout
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>

            {/* Information Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800 mb-1">Quote Information</p>
                  <p className="text-blue-700">
                    Items in your cart will be processed as a quote request. Our team will review your requirements and provide detailed pricing and availability information.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </UserLayout>
  );
};

export default UserCart;
